import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Rocket, Target, ChartLineUp, Bell, Buildings, MapTrifold, Scales, DownloadSimple,
  Sparkle, Lightning,
} from "@phosphor-icons/react";
import { GoogleReviews } from "../components/GoogleReviews";
import { LeadForm } from "../components/LeadForm";
import {
  useSeo, Container, PlanHero, SectionTitle, CheckItem, CrossItem, FeatureCard, FaqList, money,
} from "../components/plans/PlanShared";
import { startPlanCheckout } from "../lib/payments";

const ACCENT = "#10B981";

// Until Razorpay live keys are added, lock the paid self-serve tiers and show
// a "Free for All" ribbon instead of checkout. Flip to false once keys are live.
const PAID_LOCKED = true;

const TIERS = [
  {
    id: "free",
    name: "Starter",
    tagline: "Kick off your counselling research",
    price: 0,
    list: null,
    highlight: false,
    cta: "Start Free",
    features: [
      "MCC & State final-round cut-offs (MBBS & BDS)",
      "Browse all Government colleges — seats, location & establishment year",
      "Live counselling schedule tracker with round-wise alerts",
      "Basic NEET rank-based College Predictor",
      "Annual tuition & hostel fee snapshots",
    ],
    excluded: [
      "Private / Deemed college data",
      "All-round cut-off history",
      "Advanced & open-state predictors",
    ],
  },
  {
    id: "smart",
    name: "Smart",
    tagline: "Everything a serious aspirant needs",
    price: 500,
    list: 999,
    highlight: false,
    cta: "Get Smart",
    features: [
      "Everything in Starter, plus:",
      "Complete college data across all states — fees, seats, location",
      "AIQ & State cut-offs for ALL rounds (not just final)",
      "Full NEET rank-based College Predictor",
      "Open-State Predictor — surface non-domicile & management-eligible seats",
      "Cut-off comparison & round-wise trend charts",
    ],
    excluded: [
      "Private / Deemed advanced predictors",
      "NMC raw seat-matrix access",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "Complete control over your strategy",
    price: 999,
    list: 1999,
    highlight: true,
    cta: "Go Pro",
    features: [
      "Everything in Smart, plus:",
      "Private & Deemed college data, fees & seat details",
      "Advanced predictors — Private / Deemed / Management quota",
      "NMC raw seat-matrix access (round-wise, category-wise)",
      "Allotment tracker across all rounds",
      "Priority WhatsApp & email alerts on deadlines",
      "Downloadable, shareable college shortlists",
    ],
    excluded: [],
  },
];

const FEATURES = [
  { icon: Target, title: "Rank-Based Predictor", body: "Enter your NEET rank & category to see realistic college chances tagged High / Medium / Low." },
  { icon: MapTrifold, title: "Open-State Predictor", body: "Discover open, management & NRI-eligible seats you can access outside your home state." },
  { icon: ChartLineUp, title: "Cut-off Trends", body: "Compare colleges and track how closing ranks shift round-by-round across years." },
  { icon: Buildings, title: "Deep College Data", body: "Fees, seat matrix, location, establishment year and course-wise details in one place." },
  { icon: Bell, title: "Counselling Alerts", body: "Never miss a registration, choice-filling or allotment deadline — real-time notifications." },
  { icon: Scales, title: "Side-by-Side Compare", body: "Weigh colleges on fees, cut-offs and seats to build a confident preference list." },
];

const FAQ = [
  { q: "Is this a subscription or one-time payment?", a: "The Smart and Pro plans are one-time payments valid for the current NEET counselling season. No auto-renewals or hidden charges." },
  { q: "Do the paid plans include personal counselling calls?", a: "No — these are self-serve (DIY) plans giving you the tools and data. If you'd like an expert to plan and execute your counselling for you, explore our Priority Counselling plan." },
  { q: "Can I upgrade from Smart to Pro later?", a: "Yes. You can upgrade anytime by paying the difference — just reach out to our team." },
  { q: "Which courses are covered?", a: "MBBS and BDS cut-offs and college data across MCC (All-India Quota) and all state counselling bodies, with more courses being added continuously." },
];

export default function PlansSubscription() {
  useSeo(
    "Self-Serve NEET Counselling Plans — Predictor, Cut-offs & College Data | Unicators",
    "Affordable DIY NEET UG counselling plans. Access the college predictor, all-round cut-offs, open-state predictor, seat matrix and complete college data. Free, ₹500 and ₹999 tiers.",
  );
  const [leadOpen, setLeadOpen] = useState(false);

  const buy = (tier) => {
    if (tier.id === "free") return;
    startPlanCheckout({
      planId: tier.id,
      onNotConfigured: () => {
        toast.info("Online checkout is being set up. Leave your details and we'll activate your plan.");
        setLeadOpen(true);
      },
    });
  };

  return (
    <div className="bg-white" data-testid="plans-subscription-page">
      <PlanHero
        accent={ACCENT} bgFrom="#ECFDF5" bgTo="#F0FDF4"
        eyebrow="Self-Serve · DIY Plans"
        title="Own your NEET counselling"
        highlight="on your own terms."
        subtitle="Accurate cut-offs, smart predictors and complete college data — everything you need to make confident choices, without the guesswork. Start free, upgrade only when you're ready."
        stats={[
          { value: "34+", label: "States & UTs mapped" },
          { value: "All", label: "Counselling rounds" },
          { value: "MBBS+BDS", label: "Courses covered" },
          { value: "₹0", label: "To get started" },
        ]}
      >
        <a href="#pricing" data-testid="hero-see-plans"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-white font-bold text-sm uppercase tracking-wide shadow-[3px_3px_0px_0px_#09090B] hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: ACCENT }}>
          <Sparkle size={16} weight="fill" /> See the plans
        </a>
        <Link to="/ug-neet/predict" data-testid="hero-try-predictor"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white border-2 border-zinc-200 text-[#09090B] font-bold text-sm uppercase tracking-wide hover:border-[#10B981] transition-colors">
          Try the predictor
        </Link>
      </PlanHero>

      {/* Pricing */}
      <section id="pricing" className="py-16 sm:py-20">
        <Container>
          <SectionTitle center accent={ACCENT} eyebrow="Simple, honest pricing"
            title="Choose the plan that fits your journey"
            sub="These are self-service plans — no call or WhatsApp support included. For hands-on expert help, see our Priority Counselling plan." />

          <div className="grid md:grid-cols-3 gap-6 items-start">
            {TIERS.map((t) => {
              const locked = PAID_LOCKED && t.id !== "free";
              return (
              <div key={t.id} data-testid={`tier-${t.id}`}
                className={`relative rounded-3xl border p-7 flex flex-col bg-white transition-all ${t.highlight ? "border-[#10B981] shadow-[0_20px_50px_-15px_rgba(16,185,129,0.45)] md:-translate-y-3" : "border-zinc-200 shadow-sm"}`}>
                {locked && (
                  <div className="absolute inset-0 z-30 pointer-events-none overflow-hidden rounded-3xl" data-testid={`tier-locked-${t.id}`}>
                    <div className="absolute left-1/2 top-1/2 w-[200%] -translate-x-1/2 -translate-y-1/2 -rotate-[26deg] bg-red-600/95 py-2.5 shadow-xl border-y-2 border-red-700">
                      <span className="block text-center text-white text-sm font-black uppercase tracking-[0.3em] drop-shadow">Free for All</span>
                    </div>
                  </div>
                )}
                {t.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#10B981] text-white text-[10px] font-black uppercase tracking-wider">⭐ Recommended</span>
                )}
                <h3 className="text-lg font-black text-[#09090B]">{t.name}</h3>
                <p className="text-sm text-zinc-500 mt-1 min-h-[40px]">{t.tagline}</p>
                <div className="mt-4 flex items-end gap-2">
                  {t.price === 0 ? (
                    <span className="text-4xl font-black text-[#09090B] font-heading">Free</span>
                  ) : (
                    <>
                      <span className="text-4xl font-black text-[#09090B] font-heading">{money(t.price)}</span>
                      {t.list && <span className="text-base text-zinc-400 line-through mb-1">{money(t.list)}</span>}
                    </>
                  )}
                </div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mt-1">
                  {t.price === 0 ? "No credit card required" : "One-time · limited-time offer"}
                </p>

                {t.id === "free" ? (
                  <Link to="/ug-neet/predict" data-testid={`tier-cta-${t.id}`}
                    className="mt-5 h-12 rounded-full font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2 bg-zinc-900 text-white hover:bg-zinc-800 transition-colors">
                    <Rocket size={15} weight="fill" /> {t.cta}
                  </Link>
                ) : (
                  <button onClick={() => (locked ? null : buy(t))} disabled={locked} data-testid={`tier-cta-${t.id}`}
                    className={`mt-5 h-12 rounded-full font-bold text-sm uppercase tracking-wide flex items-center justify-center gap-2 transition-all ${
                      locked
                        ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
                        : t.highlight
                          ? "bg-[#10B981] text-white shadow-[3px_3px_0px_0px_#09090B] hover:-translate-y-0.5"
                          : "bg-white border-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5"
                    }`}>
                    <Lightning size={15} weight="fill" /> {t.cta}
                  </button>
                )}

                <ul className="mt-6 space-y-2.5">
                  {t.features.map((f, i) => (
                    <CheckItem key={i} accent={ACCENT}>{f}</CheckItem>
                  ))}
                  {t.excluded.map((f, i) => (
                    <CrossItem key={`x${i}`}>{f}</CrossItem>
                  ))}
                </ul>
              </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-zinc-400 mt-6">Prices are inclusive of taxes. Self-serve plans are non-refundable once activated.</p>
        </Container>
      </section>

      {/* Feature grid */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-[#F0FDF4] to-white">
        <Container>
          <SectionTitle center accent={ACCENT} eyebrow="What you unlock"
            title="Powerful tools, built for NEET counselling"
            sub="Every feature is designed to turn raw data into confident, seat-winning decisions." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (<FeatureCard key={f.title} {...f} accent={ACCENT} />))}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20">
        <Container>
          <SectionTitle accent={ACCENT} eyebrow="Good to know" title="Frequently asked questions" />
          <FaqList items={FAQ} accent={ACCENT} />
        </Container>
      </section>

      <GoogleReviews />
      <LeadForm open={leadOpen} onOpenChange={setLeadOpen} plan="Self-Serve Subscription" />
    </div>
  );
}
